/**
 * Custom hook for message data management
 * Integrates TanStack Query with Zustand store
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  thoughtProcessQueryOptions,
  ThoughtProcessParams,
} from '../api/messages';
import {
  ThoughtProcessData,
  useThoughtProcessSelectors,
  useThoughtProcessStore,
} from '../stores/thoughtProcessStore';
import { useConversationStore } from '../stores/conversationStore';

export function useMessages(params: ThoughtProcessParams) {
  return useQuery(thoughtProcessQueryOptions(params));
}

export function useMessagesWithStore(initialParams: ThoughtProcessParams) {
  const setThoughtProcessAction = useThoughtProcessStore(
    (state) => state.setThoughtProcess,
  );
  const setLoadingAction = useThoughtProcessStore(
    useShallow((state) => state.setLoading),
  );

  const { messageID, conversationID } = useThoughtProcessStore(
    useShallow((state) => ({
      messageID: state.messageID,
      conversationID: state.conversationID,
    })),
  );

  const params = {
    messageID: messageID || initialParams.messageID,
    conversationID: conversationID || initialParams.conversationID,
  };

  const query = useMessages(params);

  const {
    setMessageID, setConversationID, openPanel, closePanel,
  } = useThoughtProcessStore(
    useShallow((state) => ({
      setMessageID: state.setMessageID,
      setConversationID: state.setConversationID,
      openPanel: state.openPanel,
      closePanel: state.closePanel,
      setLoading: state.setLoading,
    })),
  );

  const { useIsLoading, useIsOpen, useCurrentThoughtProcess } = useThoughtProcessSelectors;
  const storeLoading = useIsLoading();
  const isOpen = useIsOpen();
  const currentThoughtProcess = useCurrentThoughtProcess();

  const currentConversationId = useConversationStore(
    useShallow((state) => state.selectedConversationId),
  );

  const setThoughtProcess = useCallback(
    (data: ThoughtProcessData) => {
      setThoughtProcessAction(data);
    },
    [setThoughtProcessAction],
  );

  useEffect(() => {
    if (query.data) {
      setThoughtProcess(query.data);
    }
  }, [query.data, setThoughtProcess]);

  const setLoading = useCallback(
    (loading: boolean) => {
      setLoadingAction(loading);
    },
    [setLoadingAction],
  );

  useEffect(() => {
    if (!query.isFetching && !query.isPending) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [query.isFetching, query.isPending, setLoading]);

  useEffect(() => {
    if (currentConversationId && currentConversationId !== conversationID) {
      closePanel();
    }
  }, [currentConversationId, conversationID, closePanel]);

  return {
    query,
    setMessageID,
    setConversationID,
    isOpen,
    currentThoughtProcess,
    openPanel,
    closePanel,
    setLoading,
    setThoughtProcess,
    isLoading: storeLoading || query.isFetching || query.isPending,
  };
}
